import type { ComponentPropsWithoutRef } from 'react'
import { buildImgSrc } from '@/helpers/image-url'
import { useIntl } from '@/intl'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { useEnv } from '@/scopes/common/infrastructure/env'
import {
  mqMin,
  radius,
  space,
  tok,
  tokPx,
  withStyleModifiers,
} from '@sunwebgroup/ui-components'
import { Icon, Layout } from '@sunwebgroup/ui-components/uds'
import { styled } from 'styled-components'
import { FooterBlock, FooterBlockHeader } from './Blocks'
import { FooterList, FooterListItem } from './List'

const Block = styled(FooterBlock)``
const Header = styled(FooterBlockHeader)``
const ListItem = FooterListItem

const List = styled(FooterList)`
  display: flex;
  justify-content: center;
`

const StyledFooter = styled.footer`
  padding-top: ${tokPx('semantics_layouts_s_margin_top_and_bottom')};
  padding-bottom: ${tokPx('semantics_layouts_s_margin_top_and_bottom')};
`

const StyledLayout = styled(Layout)`
  background-color: ${tok('primitives_color_neutral_100')};

  @media ${mqMin('primitives_breakpoint_m_min')} {
    background-color: initial;
  }
`

const Wrapper = styled.div`
  border-radius: ${radius('semantics_boxes_corner_radius')};
  background-color: ${tok('primitives_color_neutral_100')};
  padding: ${space('primitives_space_16')};

  @media ${mqMin('primitives_breakpoint_m_min')} {
    padding-left: ${space('primitives_space_32')};
    padding-right: ${space('primitives_space_32')};
  }
}
`

const StyledContent = styled.div`
  --min-section-size: 275px;
  --gap: ${space('primitives_space_48')};
  display: grid;
  gap: var(--gap);
  grid-gap: var(--gap);
  grid-template-columns: repeat(auto-fit, minmax(var(--min-section-size), 1fr));

  @media ${mqMin('primitives_breakpoint_m_min')} {
    --gap: ${space('primitives_space_32')};
  }
  @media ${mqMin('primitives_breakpoint_l_min')} {
    grid-template-columns: repeat(3, 1fr);
  }

  /* External links (socials, downloads, logos) */
  &:last-of-type {
    justify-items: center;

    ${Block}:last-of-type {
      grid-column: 1 / -1;
    }

    @media ${mqMin('primitives_breakpoint_l_min')} {
      justify-items: unset;
      ${Block}:last-of-type {
        grid-column: auto;
      }
    }
  }
`

const FooterBottomContent = styled(StyledContent)`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;

  ${Header} {
    text-align: center;
  }
`

const StyledLine = styled.hr`
  border-color: ${tok('semantics_dividers_color')};
  margin: ${space('primitives_space_24')} auto;
  @media ${mqMin('primitives_breakpoint_m_min')} {
    margin: ${space('primitives_space_40')} auto;
  }
`

const SocialsBlock = styled.section`
  ${Header} {
    display: none;
  }
  ${Icon} {
    ${Icon.modSize(40)};
  }

  @media ${mqMin('primitives_breakpoint_m_min')} {
    ${Header} {
      display: block;
    }
    ${List} {
      gap: ${space('primitives_space_24')};
    }
    ${Icon} {
      ${Icon.modSize(24)};
    }
  }
`

const StyledDownloadBadge = styled.img`
  height: auto;
  max-height: ${space('primitives_space_48')};
  max-width: 100%;
  width: auto;
`

const StyledLogo = styled.img`
  height: 24px;
  max-height: ${space('primitives_space_48')}; // Ensure consistent size
  max-width: 100%;
  width: auto;
`

const AppLinksBlock = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${space('primitives_space_16')};
`

function Footer({ children, ...attrs }: ComponentPropsWithoutRef<'footer'>) {
  const intl = useIntl()

  const mediaServerBaseUrl = useEnv('MEDIA_SERVER_BASE_URL')
  const links = useConfiguration('links')
  const paymentMethods = useConfiguration('paymentMethods')
  const affiliates = useConfiguration('affiliates')

  return (
    <StyledFooter {...attrs}>
      <StyledLayout>
        <Wrapper>
          {/* This content can differ per page */}
          <StyledContent>{children}</StyledContent>

          <StyledLine />

          <FooterBottomContent>
            <SocialsBlock>
              <Header>{intl.formatMessage({ id: 'footer_us_find_on_social_media' })}</Header>
              <List direction="x" spacing={space('primitives_space_48')}>
                <ListItem href={links.facebook}>
                  <Icon glyph="LogoFacebook" />
                </ListItem>
                <ListItem href={links.instagram}>
                  <Icon glyph="LogoInstagram" />
                </ListItem>
                <ListItem href={links.youtube}>
                  <Icon glyph="LogoYoutube" />
                </ListItem>
              </List>
            </SocialsBlock>
            {paymentMethods && (
              <Block>
                <Header>{intl.formatMessage({ id: 'footer_payment' })}</Header>
                <List direction="x" spacing={space('primitives_space_16')}>
                  {paymentMethods.map((payment, index) =>
                    payment.link
                      ? (
                          <ListItem key={index} href={payment.link}>
                            <StyledLogo src={buildImgSrc(`${payment.image}`, 85)} alt={payment.altText} loading="lazy" />
                          </ListItem>
                        )
                      : (
                          <StyledLogo
                            key={index}
                            src={buildImgSrc(`${payment.image}`, 85)}
                            alt={payment.altText}
                            loading="lazy"
                          />
                        ),
                  )}
                </List>
              </Block>
            )}
            <Block>
              <Header>{intl.formatMessage({ id: 'footer_affiliated_with' })}</Header>
              <List direction="x" spacing={space('primitives_space_16')}>
                {affiliates.map((affiliate, index) =>
                  affiliate.link
                    ? (
                        <ListItem key={index} href={affiliate.link}>
                          <StyledLogo src={buildImgSrc(`${affiliate.image}`, 85)} alt={affiliate.altText} loading="lazy" />
                        </ListItem>
                      )
                    : (
                        <StyledLogo
                          key={index}
                          src={buildImgSrc(`${affiliate.image}`, 85)}
                          alt={affiliate.altText}
                          loading="lazy"
                        />
                      ),
                )}
              </List>
            </Block>
            {' '}
            <Block>
              <Header>{intl.formatMessage({ id: 'footer_sustainability' })}</Header>
              <List direction="x" spacing={space('primitives_space_16')}>
                <StyledLogo
                  src={buildImgSrc(`/-/media/sustainability/travelife.ashx`, 85)}
                  alt="sustainability"
                  loading="lazy"
                />
              </List>
            </Block>
          </FooterBottomContent>
          <AppLinksBlock>
            <Block>
              <List direction="x" spacing={space('primitives_space_4')}>
                <ListItem href={links.appleStore}>
                  <StyledDownloadBadge
                    src={buildImgSrc(`${mediaServerBaseUrl}/footer/download_app_store.png`, 200)}
                    alt={intl.formatMessage({ id: 'footer_download_app_store' })}
                    loading="lazy"
                  />
                </ListItem>
                <ListItem href={links.googlePlayStore}>
                  <StyledDownloadBadge
                    src={buildImgSrc(`${mediaServerBaseUrl}/footer/download_play_store.png`, 200)}
                    alt={intl.formatMessage({ id: 'footer_download_google_play' })}
                    loading="lazy"
                  />
                </ListItem>
              </List>
            </Block>
          </AppLinksBlock>
        </Wrapper>
      </StyledLayout>
    </StyledFooter>
  )
}

export default withStyleModifiers(styled(Footer)``, { Block, Header, ListItem, List })
